// HomePage.jsx
import React, { useMemo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaAws, FaComments, FaCode, FaBrain, FaShapes, FaLink, FaProjectDiagram,
  FaSitemap, FaRobot, FaDatabase, FaExchangeAlt, FaSyncAlt, FaLaptopCode,
  FaCogs, FaJava,
} from "react-icons/fa";
import {
  SiAngular, SiMicrosoftazure, SiCsharp, SiApachecassandra, SiDocker,
  SiElasticsearch, SiHibernate, SiJavascript, SiApachekafka, SiKubernetes,
  SiMongodb, SiPython, SiReact, SiSpring, SiTerraform,
} from "react-icons/si";

/* =========================================
   Shared data
========================================= */
const ALL_TOPICS = [
  { label: "AWS", to: "/amazonWebServices" },
  { label: "Angular", to: "/angular" },
  { label: "Azure", to: "/azure" },
  { label: "Behavioral", to: "/behavioral" },
  { label: "C#", to: "/csharp" },
  { label: "Cassandra", to: "/cassandra" },
  { label: "Cosmos", to: "/cosmos" },
  { label: "Coding Round", to: "/codingRound" },
  { label: "Data Structures & Algorithms", to: "/dsa" },
  { label: "Design Patterns", to: "/designPatterns" },
  { label: "Docker", to: "/docker" },
  { label: "Elasticsearch", to: "/elasticSearch" },
  { label: "Hibernate", to: "/hibernate" },
  { label: "Java", to: "/java" },
  { label: "Java 8 Features", to: "/java8features" },
  { label: "JavaScript", to: "/javascript" },
  { label: "Kafka", to: "/kafka" },
  { label: "Kubernetes", to: "/kubernetes" },
  { label: "Microservices", to: "/microservices" },
  { label: "MongoDB", to: "/mongoDB" },
  { label: "Python", to: "/python" },
  { label: "REST APIs", to: "/restAPI" },
  { label: "React", to: "/react" },
  { label: "SDLC", to: "/sdlc" },
  { label: "Service Bus", to: "/serviceBus" },
  { label: "SQL", to: "/sql" },
  { label: "Spring", to: "/spring" },
  { label: "System Design", to: "/systemDesign" },
  { label: "Terraform", to: "/terraform" },
  { label: "Artificial Intelligence", to: "/artificialIntelligence" },
];

// Real brand/topic icons + brand colors instead of emoji — reads as a
// polished tech product rather than a casual list.
const ICONS = {
  Java: { Icon: FaJava, color: "#ED8B00" },
  "Java 8 Features": { Icon: FaJava, color: "#ED8B00" },
  Spring: { Icon: SiSpring, color: "#6DB33F" },
  Hibernate: { Icon: SiHibernate, color: "#59666C" },
  SQL: { Icon: FaDatabase, color: "#64748b" },
  JavaScript: { Icon: SiJavascript, color: "#D4AC0D" },
  React: { Icon: SiReact, color: "#149ECA" },
  Angular: { Icon: SiAngular, color: "#DD0031" },
  Kafka: { Icon: SiApachekafka, color: "#231F20" },
  "Design Patterns": { Icon: FaShapes, color: "#64748b" },
  "Data Structures & Algorithms": { Icon: FaBrain, color: "#7c3aed" },
  "REST APIs": { Icon: FaLink, color: "#64748b" },
  Microservices: { Icon: FaProjectDiagram, color: "#0ea5e9" },
  Docker: { Icon: SiDocker, color: "#2496ED" },
  Kubernetes: { Icon: SiKubernetes, color: "#326CE5" },
  AWS: { Icon: FaAws, color: "#FF9900" },
  Azure: { Icon: SiMicrosoftazure, color: "#0078D4" },
  MongoDB: { Icon: SiMongodb, color: "#47A248" },
  Elasticsearch: { Icon: SiElasticsearch, color: "#00BFB3" },
  SDLC: { Icon: FaSyncAlt, color: "#64748b" },
  "System Design": { Icon: FaSitemap, color: "#0f172a" },
  "Coding Round": { Icon: FaCode, color: "#64748b" },
  Behavioral: { Icon: FaComments, color: "#64748b" },
  Cosmos: { Icon: FaDatabase, color: "#0078D4" },
  "Service Bus": { Icon: FaExchangeAlt, color: "#0078D4" },
  Python: { Icon: SiPython, color: "#3776AB" },
  "C#": { Icon: SiCsharp, color: "#239120" },
  "Artificial Intelligence": { Icon: FaRobot, color: "#7c3aed" },
  Cassandra: { Icon: SiApachecassandra, color: "#1287B1" },
  Terraform: { Icon: SiTerraform, color: "#7B42BC" },
};
const DEFAULT_ICON = { Icon: FaDatabase, color: "#64748b" };

const FEATURE_CARDS = [
  {
    title: "Full-Stack Development",
    text:
      "Build production-grade UIs and APIs. Learn patterns that improve UX, maintainability, and performance across the stack.",
    Icon: FaLaptopCode,
    color: "#2563eb",
  },
  {
    title: "DevOps & Systems",
    text:
      "Ship faster with CI/CD, observability, containers, and cloud. Automate safely from commit to deploy.",
    Icon: FaCogs,
    color: "#0ea5e9",
  },
  {
    title: "Gen-AI Development",
    text:
      "Use LLMs responsibly, design robust APIs, and scale services. Master auth, data, and cost-efficient inference.",
    Icon: FaRobot,
    color: "#6d28d9",
  },
];

/* =========================================
   Small utilities
========================================= */
const wc = (s) => s.trim().split(/\s+/).length;
const byWordCountThenAZ = (a, b) => (wc(a.label) - wc(b.label)) || a.label.localeCompare(b.label);

/* =========================================
   Grouping
========================================= */
const GROUPS = {
  "Frontend": ["Angular", "React", "JavaScript"],
  "Backend": [
    "Java", "Java 8 Features", "Spring", "Hibernate", "REST APIs",
    "Microservices", "Design Patterns", "System Design", "Coding Round",
    "SDLC", "Python", "C#"
  ],
  "Databases": ["SQL", "MongoDB", "Cassandra", "Cosmos", "Elasticsearch"],
  "MessageQueues": ["Kafka", "Service Bus"],
  "Infrastructure": ["AWS", "Azure", "Docker", "Kubernetes", "Terraform"],
  "ArtificialIntelligence": ["Artificial Intelligence"],
  "Other": ["Behavioral"],
};

function partitionIntoGroups(list) {
  const out = { Frontend: [], Backend: [], Databases: [], MessageQueues: [], Infrastructure: [], ArtificialIntelligence: [], Other: [] };
  const labelToTopic = Object.fromEntries(list.map(t => [t.label, t]));
  Object.entries(GROUPS).forEach(([g, labels]) => {
    labels.forEach(l => labelToTopic[l] && out[g].push(labelToTopic[l]));
  });
  // anything not listed above goes to Other (future-proof)
  const used = new Set(Object.values(out).flat().map(t => t.label));
  list.forEach(t => { if (!used.has(t.label)) out.Other.push(t); });
  // sort each group: single-word first, then A→Z
  Object.keys(out).forEach(k => out[k].sort(byWordCountThenAZ));
  return out;
}

/* =========================================
   AdSlot (optional)
========================================= */
function AdSlot({ className = "my-4" }) {
  useEffect(() => {
    try { window.adsbygoogle?.push?.({}); } catch {}
  }, []);
  return (
    <div className={className}>
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client="ca-pub-9033879555371063"
        data-ad-slot="0000000000"
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}

/* =========================================
   Card components
========================================= */
function TopicCard({ label, to }) {
  const { Icon, color } = ICONS[label] || DEFAULT_ICON;
  return (
    <Link to={to} className="topic-card" aria-label={`Open ${label}`}>
      <div
        className="topic-icon"
        aria-hidden="true"
        style={{ background: `${color}1a`, color }}
      >
        <Icon size={20} />
      </div>
      <div className="topic-meta">
        <div className="topic-title">{label}</div>
        <div className="topic-sub">Start here</div>
      </div>
      <div className="topic-arrow" aria-hidden="true">›</div>
    </Link>
  );
}

function Group({ title, items }) {
  if (!items.length) return null;
  return (
    <section className="group">
      <header className="group-head">
        <h3>{title}</h3>
      </header>
      {/* Strict CSS Grid → perfect alignment */}
      <div className="topic-grid">
        {items.map((t) => <TopicCard key={t.to} {...t} />)}
      </div>
    </section>
  );
}

/* =========================================
   Page sections
========================================= */
function Hero() {
  return (
    <header className="hero">
      <div className="hero-inner">
        <span className="badge">TechVenture LLC</span>
        <h1>Learn • Practice • Get Certified</h1>
        <p>Curated roadmaps, interview prep, hands-on labs, and shareable certificates — everything you need to level up, fast.</p>
        <div className="hero-cta">
          <Link to="/learn" className="btn btn-light">Start Learning</Link>
          <Link to="/proficiencyTest" className="btn btn-outline">Get Certified</Link>
        </div>
        <small className="hero-note">No sign-up required • Free basics • New content weekly</small>
      </div>
    </header>
  );
}

function StatsStrip() {
  const stats = [
    ["Learners", "25K+"],
    ["Interview Qs", "900+"],
    ["Hands-on Labs", "120+"],
    ["Certificates", "30+"],
  ];
  return (
    <section className="stats">
      {stats.map(([k, v]) => (
        <div key={k} className="stat">
          <div className="stat-v">{v}</div>
          <div className="stat-k">{k}</div>
        </div>
      ))}
    </section>
  );
}

function BrowseTopics() {
  const [q, setQ] = useState("");
  const groups = useMemo(() => partitionIntoGroups(ALL_TOPICS), []);

  // filter by query across groups
  const filtered = useMemo(() => {
    if (!q.trim()) return groups;
    const n = q.trim().toLowerCase();
    const pick = (arr) => arr.filter(t => t.label.toLowerCase().includes(n));
    return {
      Frontend: pick(groups.Frontend),
      Backend: pick(groups.Backend),
      Databases: pick(groups.Databases),
      MessageQueues: pick(groups.MessageQueues),
      Infrastructure: pick(groups.Infrastructure),
      ArtificialIntelligence: pick(groups.ArtificialIntelligence),
      Other: pick(groups.Other),
    };
  }, [q, groups]);

  return (
    <section className="browse">
      <div className="browse-head">
        <h2>Browse Topics</h2>
        <input
          className="browse-filter"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          placeholder="Filter topics…"
          aria-label="Filter topics"
        />
      </div>

      <Group title="Frontend" items={filtered.Frontend} />
      <Group title="Backend" items={filtered.Backend} />
      <Group title="Databases" items={filtered.Databases} />
      <Group title="Message Queues" items={filtered.MessageQueues} />
      <Group title="Infrastructure" items={filtered.Infrastructure} />
      <Group title="Artificial Intelligence" items={filtered.ArtificialIntelligence} />
      <Group title="Other" items={filtered.Other} />
    </section>
  );
}

function FeatureCards() {
  return (
    <section className="features">
      {FEATURE_CARDS.map((f) => (
        <article className="feature" key={f.title}>
          <div className="feature-media" style={{ background: `${f.color}14` }}>
            <f.Icon size={40} color={f.color} aria-hidden="true" />
          </div>
          <div className="feature-body">
            <h3>{f.title}</h3>
            <p>{f.text}</p>
          </div>
        </article>
      ))}
    </section>
  );
}

function CTA() {
  return (
    <section className="cta">
      <h2>Ready to level up?</h2>
      <p>Pick a path and start learning in minutes.</p>
      <div className="cta-row">
        <Link to="/proficiencyTest" className="btn btn-primary">Start Assessment</Link>
        <Link to="/badges" className="btn btn-outline">Browse Badges</Link>
      </div>
    </section>
  );
}

/* =========================================
   Main
========================================= */
export default function HomePage() {
  return (
    <div className="home">
      {/* Inline CSS keeps alignment fix self-contained */}
      <style>{css}</style>

      <Hero />
      <StatsStrip />
      <div className="container">
        {/* <AdSlot /> */}
        <BrowseTopics />
        <FeatureCards />
        <AdSlot />
      </div>
      <CTA />
    </div>
  );
}

/* =========================================
   Styles (CSS Grid + equal-height cards)
========================================= */
const css = `
.home{
  --bg:var(--tv-bg);
  --ink:var(--tv-ink);
  --muted:var(--tv-muted);
  --brand:var(--tv-brand);
  --card:var(--tv-card);
  --border:var(--tv-border);
  --shadow:var(--tv-shadow);
}

*{box-sizing:border-box}
img{max-width:100%;display:block}
a{text-decoration:none}

.container{max-width:1100px;margin:0 auto;padding:0 16px}

/* Hero */
.hero{
  color:#fff;
  background:
    radial-gradient(1200px 600px at 10% -20%, rgba(109,40,217,.7) 0, rgba(109,40,217,.2) 50%, transparent 60%),
    linear-gradient(135deg,#0ea5e9,#6d28d9);
  padding:48px 16px 56px;
  text-align:center;
}
.hero .badge{display:inline-block;background:#fff;color:#111827;padding:6px 10px;border-radius:999px;margin-bottom:10px;font-weight:600}
.hero h1{font-family:var(--tv-font-serif);font-weight:600;font-size:clamp(28px,4vw,44px);margin:6px 0 10px}
.hero p{max-width:780px;margin:0 auto 16px;font-size:clamp(15px,2.2vw,18px);opacity:.95}
.hero-cta{display:flex;gap:10px;justify-content:center;flex-wrap:wrap;margin:8px 0 10px}
.btn{border-radius:10px;padding:10px 16px;font-weight:600;display:inline-block;border:1px solid transparent}
.btn-light{background:#fff;color:#111827}
.btn-outline{border-color:#fff;color:#fff;background:transparent}
.btn-primary{background:var(--brand);color:#fff}
.hero-search{display:flex;justify-content:center;margin-top:14px}
.hero-search input{width:100%;max-width:720px;padding:14px 16px;border-radius:12px;border:0;outline:none}
.hero-note{display:block;margin-top:8px;opacity:.75}

/* Stats */
.stats{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;padding:18px 16px;border-bottom:1px solid var(--border);background:var(--tv-bg-soft)}
@media(min-width:768px){.stats{grid-template-columns:repeat(4,1fr)}}
.stat{background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px;text-align:center;box-shadow:var(--shadow)}
.stat-v{font-weight:800;font-size:22px}
.stat-k{color:var(--muted);font-size:12px}

/* Browse */
.browse{padding:28px 0}
.browse-head{display:flex;gap:12px;align-items:center;flex-wrap:wrap;justify-content:space-between;margin-bottom:14px}
.browse h2{margin:0;font-family:var(--tv-font-serif);font-weight:600;font-size:24px}
.browse-filter{padding:8px 12px;border:1px solid var(--border);border-radius:10px;min-width:220px}

.group{margin-top:18px}
.group-head h3{font-size:15px;text-transform:uppercase;letter-spacing:.03em;color:var(--muted);margin:0 0 8px}

/* Grid with perfect alignment */
.topic-grid{
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(220px,1fr));
  gap:16px;
}
@media(max-width:420px){
  .topic-grid{grid-template-columns:1fr;}
}

/* Card with fixed, equal height */
.topic-card{
  display:flex;
  align-items:center;
  gap:12px;
  min-height:96px;            /* ensures every tile equals height */
  padding:14px 12px;
  background:var(--card);
  border:1px solid var(--border);
  border-radius:14px;
  box-shadow:var(--shadow);
  color:var(--ink);
  transition:transform .15s ease, box-shadow .15s ease;
}
.topic-card:hover{transform:translateY(-2px);box-shadow:var(--tv-shadow-lg)}
.topic-icon{
  width:42px;height:42px;border-radius:50%;
  background:var(--tv-bg-soft);display:flex;align-items:center;justify-content:center;
  font-size:22px;flex:0 0 auto;
}
.topic-meta{flex:1 1 auto;min-width:0}
.topic-title{font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.topic-sub{color:var(--muted);font-size:12px}
.topic-arrow{color:var(--muted);font-size:20px}

/* Features */
.features{display:grid;gap:16px;margin:26px 0}
@media(min-width:768px){.features{grid-template-columns:repeat(3,1fr)}}
.feature{display:flex;flex-direction:column;background:var(--card);border:1px solid var(--border);border-radius:14px;overflow:hidden;box-shadow:var(--shadow)}
.feature-media{aspect-ratio:16/9;display:flex;align-items:center;justify-content:center}
.feature-body{padding:14px}
.feature-body h3{margin:0 0 6px;font-family:var(--tv-font-serif);font-weight:600;font-size:19px}
.feature-body p{margin:0;color:var(--muted)}

/* CTA */
.cta{padding:36px 16px;text-align:center;background:var(--tv-chrome-bg);color:var(--tv-chrome-ink)}
.cta h2{margin:0 0 6px;font-family:var(--tv-font-serif);font-weight:600}
.cta p{margin:0 0 12px;color:var(--tv-chrome-muted)}
.cta-row{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}
`;
